import { createContext, useContext, useEffect, useState } from 'react';
import { fetchMe, fetchTenant, type TenantInfo } from './api';
import { AdminApp } from './admin/app';
import { Shell } from './components';
import { AccountPage, LegalPage } from './pages/account';
import { CheckoutPage } from './pages/checkout';
import { CoursePage } from './pages/course';
import { LibraryPage } from './pages/library';
import { LoginPage, RedeemPage } from './pages/login';
import { LandingPage, LegalPage as SiteLegalPage, SiteNotFound, TrialPage } from './pages/site';
import { OperatorApp, isOperatorHost } from './operator/app';
import { navigate, usePath } from './router';
import { applyTheme } from './theme';

export interface Session {
  loggedIn: boolean;
  name: string | null;
  email: string | null;
  refresh: () => Promise<void>;
}

export const TenantContext = createContext<TenantInfo | null>(null);
export const SessionContext = createContext<Session>({ loggedIn: false, name: null, email: null, refresh: async () => undefined });

export const useTenant = (): TenantInfo => useContext(TenantContext)!;
export const useSession = (): Session => useContext(SessionContext);

/**
 * Seiten der Lernenden (Konzept 4):
 *   /                          Kursbibliothek
 *   /anmelden, /anmelden/<t>   Anmeldung per Link oder Code
 *   /kurs/<slug>               Kursübersicht
 *   /kurs/<slug>/lektion/<n>   Lektion als Fenster über der Übersicht
 *   /konto                     Konto, Geräte, Käufe
 *   /impressum, /datenschutz   Rechtstexte des Anbieters
 */
type Route =
  | { page: 'library' }
  | { page: 'login' }
  | { page: 'redeem'; token: string }
  | { page: 'course'; slug: string; lesson: number | null }
  | { page: 'account' }
  | { page: 'checkout'; product: string }
  | { page: 'legal'; kind: 'impressum' | 'datenschutz' }
  | { page: 'none' };

function routeFor(pathname: string): Route {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/') return { page: 'library' };
  if (p === '/anmelden') return { page: 'login' };
  const redeem = /^\/anmelden\/([A-Za-z0-9_-]+)$/.exec(p);
  if (redeem) return { page: 'redeem', token: redeem[1]! };
  const course = /^\/kurs\/([a-z0-9-]+)(?:\/lektion\/(\d+))?$/.exec(p);
  if (course) return { page: 'course', slug: course[1]!, lesson: course[2] ? Number(course[2]) : null };
  if (p === '/konto') return { page: 'account' };
  const buy = /^\/kaufen\/([a-z0-9-]+)$/.exec(p);
  if (buy) return { page: 'checkout', product: buy[1]! };
  if (p === '/impressum') return { page: 'legal', kind: 'impressum' };
  if (p === '/datenschutz') return { page: 'legal', kind: 'datenschutz' };
  return { page: 'none' };
}

/**
 * Die Plattform selbst (evercourse.de) hat keinen Anbieter – dort steht das Schaufenster. Vorgerenderte Seiten tragen
 * `data-site="platform"` am Wurzelelement (web/scripts/prerender.mjs); für andere Pfade sagt es der Server über
 * /api/tenant (404 mit error "platform").
 */
function isPrerenderedPlatformPage(): boolean {
  return document.getElementById('root')?.dataset['site'] === 'platform';
}

function SitePages({ path }: { path: string }) {
  if (path === '/' || path === '') return <LandingPage />;
  if (path === '/kostenlos-testen') return <TrialPage />;
  if (path === '/impressum') return <SiteLegalPage kind="impressum" />;
  if (path === '/datenschutz') return <SiteLegalPage kind="datenschutz" />;
  return <SiteNotFound />;
}

export function App() {
  const path = usePath();
  const route = routeFor(path);
  const operator = isOperatorHost(window.location.hostname);
  const [platform, setPlatform] = useState<boolean>(() => isPrerenderedPlatformPage());
  const [tenant, setTenant] = useState<TenantInfo | null | undefined>(undefined);
  const [me, setMe] = useState<{ loggedIn: boolean; name: string | null; email: string | null } | undefined>(undefined);

  useEffect(() => {
    if (operator || platform) return;
    fetchTenant()
      .then((t) => {
        if (t === 'platform') {
          setPlatform(true);
          return;
        }
        setTenant(t);
        if (t) {
          applyTheme(t.theme);
          document.title = t.name;
        }
      })
      .catch(() => setTenant(null));
  }, []);

  const refresh = async () => {
    const r = await fetchMe().catch((): { loggedIn: boolean; name?: string | null; email?: string } => ({ loggedIn: false }));
    setMe({ loggedIn: r.loggedIn, name: r.name ?? null, email: r.email ?? null });
  };
  useEffect(() => {
    if (!operator && !platform) void refresh();
  }, [operator, platform]);

  // Die Hausverwaltung (verwaltung.<Plattform>) hat keinen Anbieter – eigener Login, eigene Oberfläche.
  if (operator) return <OperatorApp />;
  if (platform) return <SitePages path={path.replace(/\/+$/, '')} />;

  if (tenant === undefined || me === undefined) return null;
  if (tenant === null) {
    return (
      <main className="page page--message">
        <h1>Unter dieser Adresse gibt es keine Akademie</h1>
        <p className="muted">Bitte prüfe den Link, den du bekommen hast.</p>
      </main>
    );
  }

  const session: Session = { ...me, refresh };

  // Die Verwaltung des Anbieters wohnt unter /verwaltung – eigener Login, eigene Oberfläche, derselbe Anbieter.
  if (path === '/verwaltung' || path.startsWith('/verwaltung/')) {
    return (
      <TenantContext.Provider value={tenant}>
        <SessionContext.Provider value={session}>
          <AdminApp />
        </SessionContext.Provider>
      </TenantContext.Provider>
    );
  }

  let content: React.ReactNode;
  switch (route.page) {
    case 'login':
      content = me.loggedIn ? <Redirect to="/" /> : <LoginPage />;
      break;
    case 'redeem':
      content = <RedeemPage token={route.token} />;
      break;
    case 'library':
      content = me.loggedIn ? <LibraryPage /> : <LoginPage />;
      break;
    case 'course':
      content = <CoursePage slug={route.slug} lessonNumber={route.lesson} />;
      break;
    case 'account':
      content = me.loggedIn ? <AccountPage /> : <LoginPage />;
      break;
    case 'checkout':
      content = <CheckoutPage productSlug={route.product} />;
      break;
    case 'legal':
      content = <LegalPage kind={route.kind} />;
      break;
    default:
      content = (
        <main className="page page--message">
          <h1>Diese Seite gibt es nicht</h1>
          <p className="muted">
            <a href="/" className="link">
              Zur Bibliothek
            </a>
          </p>
        </main>
      );
  }

  return (
    <TenantContext.Provider value={tenant}>
      <SessionContext.Provider value={session}>
        <Shell>{content}</Shell>
      </SessionContext.Provider>
    </TenantContext.Provider>
  );
}

function Redirect({ to }: { to: string }) {
  useEffect(() => navigate(to, true), [to]);
  return null;
}
